/**
 * User Detail — 7-tab detail page
 *
 * Header: back link · avatar · name · email · status · risk · actions
 * Tabs:
 *   1. Profile                — identity, employment, manager, timestamps
 *   2. Roles & Groups         — effective roles with source, group memberships
 *   3. Effective Permissions  — computed flattened permission list with source
 *   4. Access & Classification — clearance, US Person, classification, export, HIPAA
 *   5. Sessions & Devices     — active sessions, terminate all, MFA reset
 *   6. Activity               — recent identity events (login, MFA, etc.)
 *   7. Audit Trail            — hash-chained per-user audit log
 *
 * Actions (header): Edit · Reset Password · Reset MFA · Terminate Sessions ·
 *                   Temporary Elevation · Impersonate · Deactivate
 */

import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, useMemo, useEffect } from 'react';
import { z } from 'zod';
import {
  ChevronLeft, User as UserIcon, UsersRound, Shield, KeyRound,
  Activity as ActivityIcon, Hash, Lock, UserCog, Pencil, ShieldAlert,
  Zap, Eye, MoreHorizontal, Mail, Phone, Building2, Calendar,
  Briefcase, Clock, CheckCircle2, XCircle, AlertTriangle, Copy,
  Monitor, Smartphone, Tablet, X as XIcon, ExternalLink, Award,
  FileText, BadgeCheck, Info, Loader2, RotateCcw,
  Send, Link2, Crown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getApiErrorCode, getApiErrorMessage } from '@/lib/api-error';
import { formatDateOnly, parseDateOnly, summariseUserAgent } from '@/lib/format';
import { toast } from 'sonner';
import {
  CLASSIFICATION_LABEL, EXPORT_CONTROL_LABEL, MFA_METHOD_LABEL,
  type User,
} from '@/lib/mock-users-data';
import {
  useUser,
  useUserLifecycle,
  useUserActivity,
  useUserAudit,
  useUserSessions,
  useRevokeImpersonationSession,
  useRevokeSession,
  useUserGroupMemberships,
  useUserRoleAssignments,
  useUnassignRoleFromUser,
  useUserEffectivePermissions,
  useResendInvitation,
  useUserPendingInvitation,
  // B2d-T1 — the per-user door onto the force-password-reset code path.
  useForcePasswordReset,
  // Review B2cT2-BF-01 — the ONE caller of POST /users/:id/reinvite, the only
  // endpoint that walks the table's `invited_revoked → invited` edge.
  useReinviteUser,
  type UserActivityEvent,
  type UserAuditEvent,
  type UserSessionRow,
  // B2b-T3 — the ONE rendering of a person's departments, shared with the
  // roster cell and the wizard summary.
  formatUserDepartments,
} from '@/lib/use-users';
import { toUserRoleRows, canRemoveRoleRow } from '@/lib/user-role-rows';
// Foundations 2 / B2a-T2 — the page renders the SERVER's projection of the
// transition table and holds no rule of its own. B2c-T3 — and what pressing a
// control does is the shared lifecycle control's, so the roster row menu and
// this action bar cannot fire the same edge two different ways.
import { LifecycleIcon, useLifecycleTransition } from '@/components/users/lifecycle-controls';
import { useHasPermission } from '@/lib/use-has-permission';
// B2e-T2 — the ownership-transfer mutation, beside the rail's other role
// writes. The page holds no rule: which people it may be offered for, and the
// phrase the confirmation demands, both arrive on the user from the server.
import { useTransferOwnership } from '@/lib/use-roles';
import { TypedConfirmDialog } from '@/components/ui/typed-confirm-dialog';
import { useAuth } from '@/contexts/auth-context';
import {
  UserStatusBadge, ClearanceBadge, EmploymentTypeBadge,
  UsPersonIndicator, MfaStatusChip, RiskScoreBadge, UserAvatar,
  RoleSourceBadge, ClassificationBadge,
} from '@/components/users/user-badges';
import { EditUserDialog } from '@/components/users/edit-user-dialog-v2';
import { AssignRoleDialog } from '@/components/users/assign-role-dialog';
import { AddToGroupDialog } from '@/components/users/add-to-group-dialog';
import { UsPersonVerificationDialog } from '@/components/users/us-person-verification-dialog';
import { StartImpersonationDialog } from '@/components/impersonation/start-impersonation-dialog';
import { InviteLinkModal, type EmailDeliveryStatus } from '@/components/users/invite-link-modal';

/**
 * URL search schema for the detail page. Activity / audit links from
 * /users/activity deep-link with `?tab=activity`, etc., so the detail
 * page must read the param and select the matching tab on mount.
 */
/**
 * Canonical detail tab values. Used by the route for tab gating + tab
 * URL deep-links from /users/activity, /trust/audit, etc.
 */
const VALID_TABS = [
  'profile',
  'roles',
  'permissions',
  'clearance',
  'sessions',
  'activity',
  'audit',
] as const;
type UserDetailTab = typeof VALID_TABS[number];

/**
 * Aliases for tab values. The visible UI label is "Access &
 * Classification" but the canonical tab value is `clearance`; deep-links
 * from external systems sometimes carry the more obvious `access`.
 * Same for `audit-trail` → `audit` and `groups` → `roles`. Map them so
 * stale links don't crash the page (closes #132).
 */
const TAB_ALIASES: Record<string, UserDetailTab> = {
  access: 'clearance',
  'access-classification': 'clearance',
  'audit-trail': 'audit',
  audits: 'audit',
  groups: 'roles', // Roles & Groups tab carries the group memberships
};

interface UserDetailSearch {
  tab?: UserDetailTab;
}

export const Route = createFileRoute('/users/$userId')({
  component: UserDetailPage,
  /**
   * Tolerant search parser — closes #132. Strict zod.parse() crashed the
   * route on any unknown ?tab= value (e.g. /users/$id?tab=access). We
   * now accept anything; valid values pass through, known aliases map
   * to their canonical tab, and unknown values fall back to undefined
   * (which the component treats as 'profile'). No more raw Zod JSON in
   * the global error boundary.
   */
  validateSearch: (raw: Record<string, unknown>): UserDetailSearch => {
    const incoming = typeof raw?.tab === 'string' ? raw.tab.trim().toLowerCase() : undefined;
    if (!incoming) return {};
    if ((VALID_TABS as readonly string[]).includes(incoming)) {
      return { tab: incoming as UserDetailTab };
    }
    const alias = TAB_ALIASES[incoming];
    if (alias) return { tab: alias };
    // Unknown tab — drop it. The component falls back to 'profile'.
    return {};
  },
});

function UserDetailPage() {
  const { userId } = Route.useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  // Wave 1a Stage 2 W1a-02: live data via TanStack Query for the
  // user record + lifecycle mutation + sessions (consumed inline in
  // SessionsTab via useUserSessions).
  const { user: liveUser, isLoading } = useUser(userId);
  const user = liveUser;

  const search = useSearch({ from: '/users/$userId' });
  // Detect when the URL had a raw `tab` param that survived
  // validateSearch's drop (i.e. an invalid value). When that happens
  // we fall back to Profile AND rewrite the URL without the bogus
  // param so a subsequent share-link is clean.
  // (TanStack Router exposes the validated search shape; the raw
  // `?tab=…` is reflected here via window.location for the cleanup.)
  const [tab, setTab] = useState<string>(search.tab ?? 'profile');
  // Keep the active tab in sync with the URL ?tab= param so deep-links from
  // the tenant Activity / Audit feeds land on the right tab.
  useEffect(() => {
    if (search.tab && search.tab !== tab) setTab(search.tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.tab]);
  // Closes #220: when the URL carried an unrecognised ?tab= value
  // (`/users/$id?tab=anyInvalidString`), validateSearch returns {} so
  // search.tab is undefined — the chrome would still show the bogus
  // value in the URL. On mount, replace the URL without the bogus
  // param so the Profile tab is the canonical location.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const raw = new URLSearchParams(window.location.search).get('tab');
    if (raw && !search.tab) {
      navigate({
        to: '/users/$userId',
        params: { userId },
        search: {},
        replace: true,
      } as never);
    }
    // Run once on mount + whenever the validated search-tab flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.tab, userId]);
  // Closes #220: the route's `validateSearch` drops unknown tab values,
  // but the URL itself is preserved (TanStack Router keeps the raw
  // querystring around). If anything gets the local `tab` state into
  // an unknown value, fall back to 'profile' for the Tabs `value` so
  // the content panel always has a matching <TabsContent>. Without
  // this guard a stale ?tab=anyInvalidString rendered chrome with
  // empty content instead of the intended graceful fallback.
  const activeTab: string = (VALID_TABS as readonly string[]).includes(tab)
    ? tab
    : 'profile';
  const handleTabChange = (next: string) => {
    setTab(next);
    navigate({
      to: '/users/$userId',
      params: { userId },
      search: next === 'profile' ? {} : { tab: next as UserDetailSearch['tab'] },
      replace: true,
    } as never);
  };
  const [editOpen, setEditOpen] = useState(false);
  const [assignRoleOpen, setAssignRoleOpen] = useState(false);
  const [addToGroupOpen, setAddToGroupOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-8 w-8 text-muted-foreground/40 animate-spin mb-3" />
        <p className="text-xs text-muted-foreground">Loading user…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <UserCog className="h-12 w-12 text-muted-foreground/30 mb-3" />
        <h2 className="text-sm font-semibold mb-1">User not found</h2>
        <p className="text-xs text-muted-foreground mb-4">
          The user ID <code className="text-[11px]">{userId}</code> doesn't exist.
        </p>
        <Button size="sm" onClick={() => navigate({ to: '/users' as any })}>
          <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back to Users
        </Button>
      </div>
    );
  }

  // Self-action guard — admin cannot lock/suspend/deactivate themselves
  // through this UI. Mirrors F61's separation-of-duties rule (server-
  // enforced); the UI hides the buttons so the affordance never appears.
  const isSelf = authUser.id === user.id;

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <UserHeader
        user={user}
        isSelf={isSelf}
        onBack={() => navigate({ to: '/users' as any })}
        onEdit={() => setEditOpen(true)}
        onImpersonate={() => setImpersonateOpen(true)}
      />

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6 pt-1 border-b">
            <TabsList className="h-9">
              <TabsTrigger value="profile" className="text-xs gap-1.5">
                <UserIcon className="h-3.5 w-3.5" /> Profile
              </TabsTrigger>
              <TabsTrigger value="roles" className="text-xs gap-1.5">
                <UsersRound className="h-3.5 w-3.5" /> Roles & Groups
              </TabsTrigger>
              <TabsTrigger value="permissions" className="text-xs gap-1.5">
                <KeyRound className="h-3.5 w-3.5" /> Effective Permissions
              </TabsTrigger>
              <TabsTrigger value="clearance" className="text-xs gap-1.5">
                <Shield className="h-3.5 w-3.5" /> Access & Classification
              </TabsTrigger>
              <TabsTrigger value="sessions" className="text-xs gap-1.5">
                <Monitor className="h-3.5 w-3.5" /> Sessions
              </TabsTrigger>
              <TabsTrigger value="activity" className="text-xs gap-1.5">
                <ActivityIcon className="h-3.5 w-3.5" /> Activity
              </TabsTrigger>
              <TabsTrigger value="audit" className="text-xs gap-1.5">
                <Hash className="h-3.5 w-3.5" /> Audit Trail
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-auto">
            <TabsContent value="profile" className="m-0 p-6"><ProfileTab user={user} /></TabsContent>
            <TabsContent value="roles" className="m-0 p-6">
              <RolesGroupsTab
                user={user}
                isSelf={isSelf}
                onAssignRole={() => setAssignRoleOpen(true)}
                onAddToGroup={() => setAddToGroupOpen(true)}
              />
            </TabsContent>
            <TabsContent value="permissions" className="m-0 p-6"><PermissionsTab user={user} /></TabsContent>
            <TabsContent value="clearance" className="m-0 p-6">
              <ClearanceTab user={user} onVerify={() => setVerifyOpen(true)} />
            </TabsContent>
            <TabsContent value="sessions" className="m-0 p-6">
              <SessionsTab userId={user.id} userName={user.fullName} isSelf={isSelf} />
            </TabsContent>
            <TabsContent value="activity" className="m-0 p-6">
              <ActivityTab userId={user.id} />
            </TabsContent>
            <TabsContent value="audit" className="m-0 p-6">
              <AuditTrailTab userId={user.id} />
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* ── Edit / Assign / Impersonate dialogs ────────────────────────────── */}
      <EditUserDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        user={user}
        onSaved={() => { /* Live invalidation handled by the hook */ }}
      />
      <AssignRoleDialog
        open={assignRoleOpen}
        onOpenChange={setAssignRoleOpen}
        user={user}
        onAssigned={() => { /* Mock only — Stage 3 W1a-07 lands real assignment */ }}
      />
      <AddToGroupDialog
        open={addToGroupOpen}
        onOpenChange={setAddToGroupOpen}
        user={user}
        onAdded={() => { /* Mock only — Stage 3 W1a-09 lands group mgmt */ }}
      />
      <UsPersonVerificationDialog
        open={verifyOpen}
        onOpenChange={setVerifyOpen}
        user={user}
        onVerified={() => { /* Mock only — F61 PATCH endpoint covers this */ }}
      />
      {/* B2d-T2 — the ONE impersonation dialog, with the person already
          chosen. The second dialog (components/users/impersonation-dialog.tsx,
          a simulated two-person approval that auto-approved itself) is
          deleted. */}
      <StartImpersonationDialog
        open={impersonateOpen}
        onOpenChange={setImpersonateOpen}
        subject={{
          id: user.id,
          email: user.email,
          firstName: user.firstName ?? null,
          lastName: user.lastName ?? null,
        }}
      />
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

function UserHeader({
  user, isSelf, onBack, onEdit, onImpersonate,
}: {
  user: User;
  isSelf: boolean;
  onBack: () => void;
  onEdit: () => void;
  onImpersonate: () => void;
}) {
  const navigate = useNavigate();
  // Foundations 2 / B2a-T2 — the lifecycle controls come from the server.
  //
  // What stood here was `canLock` / `canSuspend` / `canDeactivate`: three
  // `useHasPermission` calls that re-derived, in the browser, which permission
  // each transition class costs. That is the transition table's `permission`
  // field, asked a second time in a second language — and it could only ever
  // approximate the answer, because the required permission depends on the
  // (from, to) pair and this side knew only `to`.
  //
  // `GET /users/:id/lifecycle` answers the whole question — which transitions
  // exist from this account's state, which of them THIS caller may perform,
  // what each is called, whether it prompts, whether it needs a reason, what
  // to warn about, and for the ones they may not perform, why. The page
  // renders that answer and derives nothing.
  const { lifecycle, isLoading: lifecycleLoading } = useUserLifecycle(user.id);
  const canResetMfa    = useHasPermission('mfa:reset', 'users');
  const canEdit        = useHasPermission('write', 'users');
  // B2d-T2 — there is no `useHasPermission('impersonate', 'users')` here any
  // more (review B2dT2-BF-08). The permission is only the CALLER's half of the
  // rule, and gating on it alone offered the control on an Owner's page. The
  // whole rule arrives on the user itself as `user.canImpersonate`, answered by
  // `decideImpersonation` on GET /users/:id — one question, asked once, by the
  // screen and the endpoint alike.

  // B2e-T2 — and the ownership transfer, on exactly the same terms. Which
  // people it may be offered for, why it is refused when it is, and the phrase
  // the confirmation demands all arrive on `user.ownershipTransfer`, answered
  // by `decideOwnershipTransfer` on GET /users/:id. There is no
  // `useHasPermission('transfer_ownership', …)` here and there cannot be: no
  // grant confers the key, so it never reaches the browser's `permissions[]`.
  const transferOwnership = useTransferOwnership();
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const ownership = user.ownershipTransfer;

  // B2c-T3 — FIRING a transition is the shared control's job, and the roster's
  // row menu calls the same one. What this page owns is where the controls sit
  // (its action bar) and which invitation-surface destination it can walk;
  // what pressing one does — the prompt, the reason capture, the typed
  // removal gate, which endpoint — is written once in
  // `components/users/lifecycle-controls.tsx`.
  //
  // The invitation surface is handed to the runner too (review B2cT3-R3): the
  // ENDPOINT for `invited` is this page's (`INVITATION_SURFACE_ACTIONS` below,
  // review B2cT2-BF-01), but the dispatch is the runner's, like every other
  // surface. It used to be a second mechanism — this page firing `action.run`
  // itself while the runner's own `invitationActions` option sat with no
  // caller anywhere in the tree. The runner is constructed below, once that
  // registry exists.

  // F-W1a-02.1 — the per-user MFA reset endpoint is not shipped. The button
  // renders disabled with an explanatory tooltip rather than firing a toast
  // that fakes success. Honest empty state > looks-alive button.
  const mfaResetPending = true;

  // B2d-T1 — Force PW Reset IS shipped. It used to sit beside Reset MFA under
  // the same "not yet shipped" tooltip, and it had to: the bulk endpoint
  // answered 501 because nothing read `password_must_change`, so a control
  // that appeared to work would have told an administrator that a distrusted
  // credential was remediated while it still authenticated. `routes/v1/auth.ts`
  // now reads the flag at every door that mints a token, so the button fires
  // POST /users/:id/force-password-reset — the same code path the roster's
  // bulk action walks.
  const forcePwReset = useForcePasswordReset();

  // #75 / W1a-08 — the invited-user recovery group.
  //
  // This is NOT a lifecycle transition and never was: an invited person's only
  // lifecycle edges are "revoke the invitation" and "accept it", and the
  // second is theirs to walk. What the admin needs on this page is the invite
  // LINK back, because the post-Resend toast on the Invitations tab
  // auto-dismissed after 12s and lost the URL forever when SMTP was down.
  //
  // The state comes off the ROW, which is already in hand from `useUser` and
  // is the same value the badge two lines below renders — one account state on
  // this page, read synchronously (review B2aT2-BF-01).
  //
  // It briefly came from the lifecycle QUERY instead, and that was wrong in
  // the fail-open direction: `lifecycle` is `undefined` on first paint and
  // stays `undefined` forever if the request fails, so an invited account
  // rendered as NOT invited — the W1a-08 invite-recovery group vanished and
  // "Reset MFA" / "Force PW Reset" appeared for somebody who has never
  // authenticated. The row is normalised through the shared
  // `normalizeLifecycleState` in `mapApiUser`, so "one place, normalised the
  // same way the write path normalises it" is true of `user.status` too.
  const isInvited = user.status === 'invited';

  // W1a-08 invite-recovery wiring. The pending-invitation lookup is
  // gated by isInvited + canEdit so it does NOT hit the write:users
  // invitation roster for limited viewers or non-invited users.
  const {
    invitation: pendingInvitation,
    isLoading: pendingInvLoading,
  } = useUserPendingInvitation(isInvited && canEdit ? user.email : null);
  const resend = useResendInvitation();
  const [inviteModal, setInviteModal] = useState<{
    inviteUrl: string;
    expiresAt: string;
    emailDelivery: EmailDeliveryStatus;
    emailError?: string;
  } | null>(null);

  /** Build the full URL the admin can share. The API returns either an
   *  absolute URL (when APP_BASE_URL is set) or a leading-slash path
   *  fallback. We normalise to absolute here so the modal can offer a
   *  copy-friendly value regardless of deployment configuration. */
  const toAbsoluteInviteUrl = (raw: string): string =>
    raw.startsWith('http')
      ? raw
      : `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`;

  // ── Review B2cT2-BF-01 — the invitation surface's way BACK ───────────────
  //
  // A revoke is not a delete (B2c-T1): the person stays on the roster in
  // `invited_revoked`, and the table declares one edge out of it —
  // `invited_revoked → invited`, "Re-invite", surface `invitation`. The
  // endpoint that walks it shipped with no caller, while the revoke
  // confirmation, the create-user 409 and the resend 410 all told the operator
  // to use it. `PATCH /users/:id/status` cannot: `invited` is not a
  // status-surface destination, so its own body schema refuses it.
  //
  // Rendered from the SERVER's projection, like the status controls beside it:
  // the page does not decide that this control exists, what it is called, or
  // whether the caller may press it. What the page owns is which endpoint each
  // destination goes to — `INVITATION_SURFACE_ACTIONS` below — and a
  // destination with no entry is not drawn, because a control that cannot do
  // anything is the dead button this rail keeps deleting. The other
  // invitation-surface edges are somebody else's door: `invited → active` is
  // the subject's own (it is refused for every administrator), and
  // `invited → invited_revoked` is the Invitations tab's, where the invitation
  // row lives — this header already links there.
  const reinvite = useReinviteUser();

  const fireReinvite = async () => {
    try {
      const res = await reinvite.mutateAsync({ userId: user.id });
      setInviteModal({
        inviteUrl: toAbsoluteInviteUrl(res.inviteUrl),
        expiresAt: res.expiresAt,
        emailDelivery: res.emailDelivery ?? 'disabled',
        emailError: res.emailError,
      });
      toast.success(`Invitation reissued for ${res.email}`);
    } catch (err) {
      const code = getApiErrorCode(err);
      // A refused edge answers 409 INVALID_TRANSITION carrying the table's own
      // sentence; it is surfaced verbatim, as the status controls do.
      const msg = getApiErrorMessage(err, 'Could not re-invite this user');
      toast.error(`${code}: ${msg}`);
    }
  };

  /** Which endpoint each invitation-surface destination goes to. */
  const INVITATION_SURFACE_ACTIONS: Record<
    string,
    { run: () => void; pending: boolean }
  > = {
    invited: { run: () => void fireReinvite(), pending: reinvite.isPending },
  };

  // B2c-T3 — FIRING a transition is the shared control's job, and the roster's
  // row menu calls the same one. What this page owns is where the controls sit
  // (its action bar) and which invitation-surface destination it can walk;
  // what pressing one does — the prompt, the reason capture, the typed removal
  // gate, which endpoint a surface goes to — is written once in
  // `components/users/lifecycle-controls.tsx`.
  const lifecycleRunner = useLifecycleTransition(
    { id: user.id, fullName: user.fullName, email: user.email },
    {
      // The person is gone from this tenant, so the page that shows them is
      // gone with them — back to the roster rather than a detail page that
      // now 404s.
      onRemoved: () => navigate({ to: '/users' as never }),
      // Review B2cT3-R3 — the page registers its endpoint; the runner does the
      // dispatching, for this surface as for the other two.
      invitationActions: INVITATION_SURFACE_ACTIONS,
    }
  );

  const fireResend = async () => {
    if (!pendingInvitation) {
      // Should never happen — the button is hidden in this state — but
      // guard anyway so a stale render doesn't crash the page.
      toast.error('No pending invitation found for this user.');
      return;
    }
    try {
      const res = await resend.mutateAsync({ id: pendingInvitation.id });
      setInviteModal({
        inviteUrl: toAbsoluteInviteUrl(res.inviteUrl),
        expiresAt: res.expiresAt,
        // Backwards-compat: pre-W1a-08 deployments omit the field. Treat
        // missing as 'disabled' so the modal banner is honest about
        // the email-delivery state.
        emailDelivery: res.emailDelivery ?? 'disabled',
        emailError: res.emailError,
      });
    } catch (err) {
      const code = getApiErrorCode(err);
      const msg = getApiErrorMessage(err, 'Could not resend invitation');
      toast.error(`${code}: ${msg}`);
    }
  };

  return (
    <div className="border-b bg-background px-6 py-4">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        <ChevronLeft className="h-3 w-3" />
        Back to Users
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <UserAvatar name={user.fullName} color={user.avatarColor} size="lg" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-semibold truncate">{user.fullName}</h1>
              <UserStatusBadge status={user.status} />
              <RiskScoreBadge score={user.riskScore} />
              {isSelf && (
                <Badge variant="outline" className="text-[9px]">
                  This is you
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{user.email}</p>
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-2 flex-wrap">
              {user.position && (
                <span className="inline-flex items-center gap-1">
                  <Briefcase className="h-3 w-3" /> {user.position}
                </span>
              )}
              {/* B2b-T3 — the header chip carries the LIST (or "All
                  departments"), on the same line it always was. It is hidden
                  only when the store holds nothing for this person, which the
                  helper renders as an empty string here. */}
              {user.position && formatUserDepartments(user.departments, '') && <span>·</span>}
              {formatUserDepartments(user.departments, '') && (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> {formatUserDepartments(user.departments, '')}
                </span>
              )}
              {user.employeeId && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1">
                    <BadgeCheck className="h-3 w-3" /> {user.employeeId}
                  </span>
                </>
              )}
              {user.ssoLinked && (
                <>
                  <span>·</span>
                  <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
                    SSO · {user.ssoProvider}
                  </Badge>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
          {canEdit && (
            <Button size="sm" variant="outline" onClick={onEdit} title="Edit user profile">
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          )}

          {/* ── W1a-08 invited-user recovery action group ─────────────────── */}
          {/* Replaces the row of disabled lifecycle buttons when            */}
          {/* status='invited'. Primary affordance is "Resend & Get Invite  */}
          {/* Link" which opens the persistent InviteLinkModal so the admin */}
          {/* can copy the URL repeatedly even if SMTP is broken.           */}
          {isInvited && !isSelf && canEdit && (
            <>
              <Button
                size="sm"
                onClick={fireResend}
                disabled={resend.isPending || pendingInvLoading || !pendingInvitation}
                title={
                  !pendingInvitation && !pendingInvLoading
                    ? 'No pending invitation row found — use New User to re-invite.'
                    : 'Rotate the token and surface a fresh URL the admin can share manually.'
                }
              >
                {resend.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5 mr-1.5" />
                )}
                Resend & Get Invite Link
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate({ to: '/users/invitations' as any })}
                title="Open the Invitations tab"
              >
                <Link2 className="h-3.5 w-3.5 mr-1.5" /> View on Invitations
              </Button>
            </>
          )}

          {/* ── Lifecycle actions — rendered from GET /users/:id/lifecycle ── */}
          {/* B2a-T2. One control per transition the server returned, in the  */}
          {/* order the ONE table declares them. The page chooses none of it:  */}
          {/* not which controls exist, not their words, not their emphasis,   */}
          {/* not whether they prompt, and not why a refused one is refused.   */}
          {/*                                                                 */}
          {/* B2c-T3 adds the DELETE surface beside the status one. Removing  */}
          {/* somebody was reachable from the roster's bulk menu and from     */}
          {/* nowhere else: the one place an operator goes to act on one      */}
          {/* person could not do it, so the answer was "select the row on    */}
          {/* the list and use a batch action" for a single, irreversible     */}
          {/* removal. It is the table's own edge, with the table's own       */}
          {/* warning, behind the rail's typed-confirm gate.                  */}
          {!lifecycleLoading && lifecycle?.allowed
            .filter((t) => t.surface === 'status' || t.surface === 'delete')
            .map((t) => (
              <Button
                key={`allow-${t.to}`}
                size="sm"
                variant={t.severity === 'destructive' ? 'destructive' : 'outline'}
                disabled={lifecycleRunner.pending}
                onClick={() => lifecycleRunner.start(t)}
                title={t.warning ?? t.label}
              >
                <LifecycleIcon icon={t.icon} /> {t.label}
              </Button>
            ))}

          {/* ── Invitation-surface actions (review B2cT2-BF-01) ──────────── */}
          {/* The same rendering rule as the status controls above: one        */}
          {/* control per transition the server returned, in the table's own   */}
          {/* order, with the table's label, icon and emphasis. Only the       */}
          {/* destinations this page can actually walk are drawn.              */}
          {!lifecycleLoading && lifecycle?.allowed
            .filter((t) => t.surface === 'invitation'
              && lifecycleRunner.invitationAction(t.to) !== undefined)
            .map((t) => {
              const action = lifecycleRunner.invitationAction(t.to)!;
              return (
                <Button
                  key={`invite-${t.to}`}
                  size="sm"
                  variant={t.severity === 'destructive' ? 'destructive' : 'outline'}
                  disabled={action.pending}
                  onClick={() => lifecycleRunner.start(t)}
                  title={t.warning ?? t.label}
                >
                  {action.pending
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <LifecycleIcon icon={t.icon} />}
                  {' '}{t.label}
                </Button>
              );
            })}

          {/* A refused transition is SHOWN, disabled, with the server's       */}
          {/* reason as its tooltip. Hiding it is what produced the support    */}
          {/* ticket behind #75: an operator who cannot see the action cannot  */}
          {/* find out why it is unavailable.                                 */}
          {/*                                                                 */}
          {/* EXCEPT on your own page (review B2aT2-R2). The lifecycle surface */}
          {/* is not yours to act on at all — separation of duties refuses     */}
          {/* every status edge on yourself — so the answer is not "here are   */}
          {/* three dead buttons"; it is that this account's lifecycle is      */}
          {/* somebody else's to change. That is the same `!isSelf` guard the  */}
          {/* sibling Reset MFA and Force PW Reset actions below carry, and it */}
          {/* is what the page did before B2a-T2.                              */}
          {/*                                                                 */}
          {/* B2c-T3: the delete surface is refused here too, and shown for    */}
          {/* the same reason — "why can I not remove this person?" is the     */}
          {/* question an absent control cannot answer.                        */}
          {!lifecycleLoading && !isSelf && lifecycle?.refused
            .filter((t) => t.surface === 'status' || t.surface === 'delete')
            .map((t) => (
              <Button
                key={`refuse-${t.to}`}
                size="sm"
                variant="outline"
                disabled
                title={t.reason}
              >
                <LifecycleIcon icon={t.icon} /> {t.label}
              </Button>
            ))}

          {!isInvited && !isSelf && canResetMfa && (
            <Button
              size="sm"
              variant="outline"
              disabled={mfaResetPending}
              title={
                mfaResetPending
                  ? 'Backend endpoint not yet shipped — coming in F-W1a-02.1'
                  : "Reset the user's MFA enrollment"
              }
            >
              <ShieldAlert className="h-3.5 w-3.5 mr-1.5" /> Reset MFA
            </Button>
          )}

          {/* The gate is `mfa:reset:users`, which is what the endpoint costs
              — not `write:users`, which is what this button used to read
              while it did nothing. A control priced differently from its
              endpoint is a 403 an operator cannot explain. */}
          {!isInvited && !isSelf && canResetMfa && (
            <Button
              size="sm"
              variant="outline"
              disabled={forcePwReset.isPending}
              title="Require this person to set a new password at their next sign-in; ends their sessions now"
              onClick={async () => {
                try {
                  await forcePwReset.mutateAsync({
                    userId: user.id,
                    reason: 'Forced from the user detail page',
                  });
                  toast.success(
                    'Password reset forced — their sessions have ended and their next sign-in must set a new password',
                  );
                } catch (err) {
                  toast.error(
                    err instanceof Error
                      ? err.message
                      : 'Could not force a password reset',
                  );
                }
              }}
            >
              <KeyRound className="h-3.5 w-3.5 mr-1.5" />{' '}
              {forcePwReset.isPending ? 'Forcing…' : 'Force PW Reset'}
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" title="More actions">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">
                Support
              </DropdownMenuLabel>
              {/* B2d-T2 — Bug #655 is closed. The route family is mounted
                  (POST /api/v1/users/:id/impersonate, routes/v1/users-impersonation.ts)
                  and carries the whole contract the old comment said was
                  missing: the `impersonate:users` permission, a sixty-minute
                  expiry, a session row, a banner, and every action audited as
                  the impersonator. The control is gated on the SAME key the
                  endpoint charges, so it cannot offer what the server refuses,
                  and it is hidden on your own page because nobody impersonates
                  themselves (policy/impersonation.ts).

                  REVIEW B2dT2-BF-08 — AND THE SUBJECT HALF, WHICH THIS GATE
                  DID NOT ASK. `canImpersonate && !isSelf` is only the caller's
                  permission and one of the four refusals, so the item was
                  offered on an OWNER's page: an Admin clicked it, typed a
                  ten-character reason and got a 403 the screen had no way to
                  anticipate — the lying button the comment above claims was
                  removed. `user.canImpersonate` is the WHOLE rule, answered by
                  the server on GET /users/:id from `decideImpersonation`, so
                  the control and the endpoint cannot disagree. Nothing is
                  re-derived here; the browser renders the answer. */}
              {user.canImpersonate === true && (
                <DropdownMenuItem onSelect={onImpersonate} className="text-xs">
                  <Eye className="h-3.5 w-3.5 mr-2" /> Impersonate for support
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onSelect={() => {
                  navigator.clipboard.writeText(user.id);
                  toast.success('User ID copied');
                }}
                className="text-xs"
              >
                <Copy className="h-3.5 w-3.5 mr-2" /> Copy user ID
              </DropdownMenuItem>
              {/* B2e-T2 — OWNERSHIP TRANSFER. Until this shipped, ownership
                  could only be set by the install wizard: `PATCH /users/:id`
                  has no `owner` in its role enum and refuses to change an
                  Owner's role at all, so a tenant whose Owner left had no path
                  to appoint another. It is offered only where the server says
                  it is allowed — `user.ownershipTransfer.allowed`, the same
                  function the endpoint is gated by — so the item cannot appear
                  on a page where the POST would 403.

                  It sits under its own heading rather than beside Impersonate
                  because it is not a support action: it is irreversible from
                  the caller's side, which is what the typed confirmation is
                  for. The caller becomes an Admin the moment it succeeds
                  (founder, 2026-09-18 — the outgoing Owner keeps full
                  administrative power), so the dialog says so before it is
                  fired, not after. */}
              {ownership?.allowed === true && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider">
                    Ownership
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onSelect={() => {
                      setTransferError(null);
                      setTransferOpen(true);
                    }}
                    className="text-xs"
                  >
                    <Crown className="h-3.5 w-3.5 mr-2" /> Transfer ownership…
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ── Lifecycle confirmations — the ONE control, shared with the  ── */}
      {/* ── roster row menu (B2c-T3). The prompts, their words and their  */}
      {/* ── emphasis are the server entry’s; the typed gate is the rail’s  */}
      {/* ── canonical destructive one.                                    */}
      {lifecycleRunner.dialogs}

      {/* ── B2e-T2 — the ownership handover, behind the rail's canonical  ── */}
      {/* ── typed-confirm gate. `confirmPhrase` is the SERVER's (the tenant */}
      {/* ── name it will compare against), so the dialog cannot demand      */}
      {/* ── something the endpoint then rejects. `closeOnConfirm={false}`   */}
      {/* ── keeps it mounted so a 409 or a 422 lands somewhere the operator */}
      {/* ── can read it without re-typing.                                  */}
      {ownership?.allowed === true && (
        <TypedConfirmDialog
          open={transferOpen}
          onOpenChange={(open) => {
            setTransferOpen(open);
            if (!open) setTransferError(null);
          }}
          title="Transfer ownership"
          description={`Hand ${user.fullName} ownership of this organization. Ownership transfers — it is not shared.`}
          consequences={[
            `${user.fullName} becomes the Owner of this organization.`,
            'You become an Admin, keeping full administrative power.',
            'This organization will have exactly one Owner, and it will not be you.',
            'Only the new Owner can transfer ownership back.',
            'The change is audited, naming both of you.',
          ]}
          confirmPhrase={ownership.confirmPhrase}
          confirmLabel="Transfer ownership"
          variant="danger"
          closeOnConfirm={false}
          pending={transferOwnership.isPending}
          errorMessage={transferError}
          onConfirm={async () => {
            setTransferError(null);
            try {
              const result = await transferOwnership.mutateAsync({
                userId: user.id,
                confirm: ownership.confirmPhrase,
              });
              setTransferOpen(false);
              // The server names the role. Translating a role KEY into a
              // label here would be a role compare in the browser, which is
              // the derivation `no-web-derivation-lint` fails the build on —
              // and it caught exactly this line.
              toast.success(
                `${user.fullName} is now the Owner — your role is now ${result.previousOwner.roleName}, with full administrative power.`,
              );
            } catch (err) {
              setTransferError(
                getApiErrorMessage(err, 'Could not transfer ownership'),
              );
            }
          }}
        />
      )}

      {/* ── W1a-08 Invite-Recovery modal ─────────────────────────────────── */}
      {inviteModal && (
        <InviteLinkModal
          open={inviteModal !== null}
          onOpenChange={(open) => {
            if (!open) setInviteModal(null);
          }}
          inviteUrl={inviteModal.inviteUrl}
          expiresAt={inviteModal.expiresAt}
          emailDelivery={inviteModal.emailDelivery}
          emailError={inviteModal.emailError}
          recipientEmail={user.email}
          onResend={fireResend}
        />
      )}
    </div>
  );
}

// ─── Tab 1: Profile ──────────────────────────────────────────────────────────

function ProfileTab({ user }: { user: User }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 max-w-6xl">
      {/* Left column: Identity + Employment */}
      <div className="lg:col-span-2 space-y-4">
        <SectionCard title="Identity" icon={UserIcon}>
          <FieldGrid>
            <Field label="Full name" value={user.fullName} />
            <Field label="Email" value={user.email} icon={Mail} copy />
            <Field label="Employee ID" value={user.employeeId} />
            <Field label="Phone" value={user.phoneNumber ?? '—'} icon={Phone} />
            <Field label="Data region" value={user.dataRegion} />
            {user.gdprDataSubject && <Field label="GDPR subject" value="Yes" />}
          </FieldGrid>
        </SectionCard>

        <SectionCard title="Employment" icon={Briefcase}>
          <FieldGrid>
            <Field label="Position" value={user.position} />
            {/* B2b-T3 — the same Department field, on the same row, showing
                the list instead of one name. */}
            <Field label="Department" value={formatUserDepartments(user.departments)} icon={Building2} />
            <Field label="Manager" value={user.managerName ?? '—'} />
            {/* B2c-T2 — the badge renders nothing for a type nobody has
                set, so the field shows an em-dash instead of asserting one. */}
            <Field label="Employment type" value={
              user.employmentType
                ? <EmploymentTypeBadge type={user.employmentType} />
                : '—'
            } />
            <Field label="Cost center" value={user.costCenter ?? '—'} />
            {/* Use formatDateOnly / parseDateOnly so YYYY-MM-DD values
                from the bA1 DATE columns don't shift one day in
                non-UTC zones (#182). */}
            <Field label="Start date" value={formatDateOnly(user.startDate)} icon={Calendar} />
            {user.endDate && (() => {
              const ed = parseDateOnly(user.endDate);
              const expiringSoon = ed != null && ed.getTime() < Date.now() + 30 * 86400_000;
              return (
                <Field
                  label="End date"
                  value={formatDateOnly(user.endDate)}
                  icon={Calendar}
                  highlight={expiringSoon}
                />
              );
            })()}
          </FieldGrid>
        </SectionCard>

        <SectionCard title="Training & certifications" icon={Award}>
          {user.training.length === 0 ? (
            <p className="text-xs text-muted-foreground italic py-2">No training on file.</p>
          ) : (
            <div className="space-y-2">
              {user.training.map(t => {
                const statusColor = t.status === 'current'
                  ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30'
                  : t.status === 'expiring_soon'
                    ? 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30'
                    : 'text-red-700 dark:text-red-300 bg-red-500/10 border-red-500/30';
                return (
                  <div key={t.id} className="flex items-center gap-3 rounded-md border p-2.5">
                    <Award className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">{t.courseName}</div>
                      <div className="text-[10px] text-muted-foreground">
                        Completed {new Date(t.completedAt).toLocaleDateString()}
                        {t.expiresAt && <> · Expires {new Date(t.expiresAt).toLocaleDateString()}</>}
                      </div>
                    </div>
                    <Badge variant="outline" className={cn('text-[10px]', statusColor)}>
                      {t.status === 'expiring_soon' ? 'Expiring soon' : t.status}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Right column: System info + quick facts */}
      <div className="space-y-4">
        <SectionCard title="System" icon={Info}>
          <FieldGrid compact>
            <Field label="User ID" value={
              <code className="text-[10px] font-mono">{user.id}</code>
            } />
            <Field label="Created" value={new Date(user.createdAt).toLocaleDateString()} />
            <Field label="Last login" value={
              user.lastLoginAt
                ? new Date(user.lastLoginAt).toLocaleString()
                : 'Never'
            } />
            <Field label="Dormancy" value={
              !user.lastLoginAt
                ? (user.status === 'invited' ? 'Awaiting invitation acceptance' : 'Never signed in')
                : user.dormancyDays === 0 ? 'Active today' : `${user.dormancyDays} days`
            } highlight={!!user.lastLoginAt && user.dormancyDays >= 30} />
            <Field label="Password changed" value={
              user.passwordLastChangedAt
                ? new Date(user.passwordLastChangedAt).toLocaleDateString()
                : '—'
            } />
          </FieldGrid>
        </SectionCard>

        <SectionCard title="Access review" icon={Clock}>
          {user.lastReviewedAt ? (
            <FieldGrid compact>
              <Field label="Last review" value={new Date(user.lastReviewedAt).toLocaleDateString()} />
              <Field label="Reviewed by" value={user.lastReviewedBy ?? '—'} />
              <Field
                label="Next due"
                value={user.nextReviewDue ? new Date(user.nextReviewDue).toLocaleDateString() : '—'}
                highlight={user.nextReviewDue ? new Date(user.nextReviewDue).getTime() < Date.now() : false}
              />
            </FieldGrid>
          ) : (
            <p className="text-xs text-muted-foreground italic">No review on record.</p>
          )}
        </SectionCard>

        <SectionCard title="AI usage (30d)" icon={Zap}>
          <FieldGrid compact>
            <Field label="Queries" value={user.telemetry.queriesLast30d.toLocaleString()} />
            <Field label="Avg / day" value={user.telemetry.avgQueriesPerDay.toLocaleString()} />
            <Field label="Top agent" value={user.telemetry.topAgentName} />
            <Field label="Docs accessed" value={user.telemetry.documentsAccessedLast30d.toString()} />
            <Field label="Avg session" value={`${user.telemetry.avgSessionDurationMin} min`} />
            <Field label="Est. cost" value={`$${user.telemetry.estimatedCostLast30d.toFixed(2)}`} />
          </FieldGrid>
        </SectionCard>
      </div>
    </div>
  );
}

// ─── Tab 2: Roles & Groups ───────────────────────────────────────────────────

function RolesGroupsTab({
  user, isSelf, onAssignRole, onAddToGroup,
}: {
  user: User;
  isSelf: boolean;
  onAssignRole: () => void;
  onAddToGroup: () => void;
}) {
  // Live per-user hooks (resolves prior CM-3 + CM-4 backend gaps).
  // - useUserGroupMemberships → GET /users/:id/groups → real per-user
  //   group memberships with joined name + memberCount.
  // - useUserRoleAssignments → GET /users/:id/role-assignments → the direct
  //   assignments IN FORCE, projected from `resolvePrincipal` (B1-T2c), each
  //   with the roleType, permission count and source the server resolved.
  const {
    groups: memberships,
    isLoading: groupsLoading,
    error: groupsError,
  } = useUserGroupMemberships(user.id);
  const {
    assignments,
    isLoading: assignmentsLoading,
    error: assignmentsError,
  } = useUserRoleAssignments(user.id);

  // Closes #217: a direct custom-role assignment had no remove control.
  // The DELETE endpoint already exists; this surfaces it inline next
  // to the role chip with a confirmation dialog. System roles still
  // change via the Edit User dialog (separation-of-duties) so they
  // intentionally do NOT get a remove control here.
  const canAssignRole = useHasPermission('role:assign', 'users');
  const unassign = useUnassignRoleFromUser();
  const [unassignTarget, setUnassignTarget] = useState<{
    roleId: string;
    roleName: string;
  } | null>(null);
  const handleUnassignConfirm = async () => {
    if (!unassignTarget) return;
    try {
      await unassign.mutateAsync({
        userId: user.id,
        roleId: unassignTarget.roleId,
      });
      toast.success(`Removed "${unassignTarget.roleName}" from ${user.fullName}`);
      setUnassignTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not remove role'));
    }
  };

  // ONE SOURCE (PROVE-B1c-C3). Every role this person holds directly — shipped
  // and custom alike — is a row of `GET /users/:id/role-assignments`, with the
  // roleType, the permission count and the source the SERVER resolved. The tab
  // renders that answer and derives nothing of its own.
  //
  // WHAT WAS DELETED, AND WHY IT HAD TO BE. This list used to CONCATENATE a
  // row synthesised from `user.roles` (the lean membership role, its permission
  // count looked up in the `useRoles()` catalogue) with the assignment rows,
  // each stamped `roleType: 'custom'`. A comment here claimed the two sources
  // could not overlap because system roles were not stored in
  // `user_role_assignments`. Migration c27 §4 writes the platform role into
  // that table for every membership, and §5(c) refuses to drop
  // `user_organizations.role` unless every membership holds one — so the
  // overlap became total: every user rendered their shipped role twice, the
  // duplicate badged Custom (from the hardcode) with an inline Remove control
  // that `DELETE /users/:userId/roles/:roleId` answers 404 to, because a
  // platform role's organization_id is NULL.
  const renderRoles = toUserRoleRows(assignments);

  return (
    <div className="space-y-4 max-w-5xl">
      {/* The COUNT is an answer too. Printing "Roles (0)" above "could not
          load roles" states a fact derived from a read that never returned —
          the same fail-open as the body, one line up (prove-stage dissent on
          B1c criterion 2). When the read failed the heading carries no
          number at all. */}
      <SectionCard
        title={assignmentsError ? 'Roles' : `Roles (${renderRoles.length})`}
        icon={Shield}
      >
        {assignmentsError ? (
          /* A request that failed says so. "No roles assigned." is an ANSWER,
             and printing it for a read that never returned is the page
             inventing one (principles.md #4 — nothing fails open). */
          <p className="text-xs text-muted-foreground">
            {getApiErrorMessage(assignmentsError, 'Could not load roles.')}
          </p>
        ) : assignmentsLoading ? (
          <p className="text-xs text-muted-foreground italic">Loading roles…</p>
        ) : renderRoles.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No roles assigned.</p>
        ) : (
          <div className="space-y-2">
            {renderRoles.map((r) => {
              // Closes #217: a custom role gets an inline Remove control.
              // The gate reads the SERVER's roleType, so it cannot offer
              // Remove on a shipped role the DELETE path refuses (which is
              // exactly what the old hardcoded 'custom' did to every migrated
              // membership). Self-actions are blocked server-side
              // (DELETE /users/:userId/roles/:roleId returns 403); gated
              // client-side too so the button doesn't appear at all for self
              // or for callers without role:assign:users.
              const canRemoveThisRole = canRemoveRoleRow(r, {
                canAssignRole,
                isSelf,
              });
              return (
              <div key={r.keyId} className="flex items-center gap-3 rounded-md border p-3">
                <Shield className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium">{r.name}</span>
                    <RoleSourceBadge source={r.source} />
                    {r.roleType === 'system' && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">System</Badge>
                    )}
                    {r.roleType === 'custom' && (
                      <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30">Custom</Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {/*
                      A row whose assigned_at the store could not give us
                      renders as an em dash, not as 1 Jan 1970 — the API stopped
                      fabricating that timestamp (B1-T2c review R6).
                    */}
                    {r.sourceDetail} · Granted{' '}
                    {r.grantedAt ? new Date(r.grantedAt).toLocaleDateString() : '—'}
                  </div>
                </div>
                <Badge variant="outline" className="text-[10px] tabular-nums">
                  {r.permissionCount} permission{r.permissionCount === 1 ? '' : 's'}
                </Badge>
                {canRemoveThisRole && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                    title={`Remove ${r.name} from ${user.fullName}`}
                    aria-label={`Remove ${r.name}`}
                    onClick={() =>
                      setUnassignTarget({
                        roleId: r.roleId,
                        roleName: r.name,
                      })
                    }
                  >
                    <XIcon className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              );
            })}
          </div>
        )}
        <Button size="sm" variant="outline" className="mt-3" onClick={onAssignRole}>
          <Shield className="h-3.5 w-3.5 mr-1.5" /> Assign role
        </Button>
      </SectionCard>

      {/* Same rule as the Roles heading above: no count for a failed read. */}
      <SectionCard
        title={groupsError ? 'Group memberships' : `Group memberships (${memberships.length})`}
        icon={UsersRound}
      >
        {groupsError ? (
          /* Same rule as the roles half above: "Not a member of any groups."
             is an ANSWER, and printing it for a read that never returned is
             the page inventing one (principles.md #4 — nothing fails open). */
          <p className="text-xs text-muted-foreground">
            {getApiErrorMessage(groupsError, 'Could not load group memberships.')}
          </p>
        ) : groupsLoading ? (
          <p className="text-xs text-muted-foreground italic">Loading…</p>
        ) : memberships.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            Not a member of any groups.
          </p>
        ) : (
          <div className="space-y-2">
            {memberships.map((g) => (
              <div key={g.groupId} className="flex items-center gap-3 rounded-md border p-3">
                <UsersRound className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium">{g.name}</span>
                    <Badge variant="outline" className={cn(
                      'text-[9px] capitalize',
                      g.membershipType === 'manual' && 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
                      g.membershipType === 'dynamic' && 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30',
                    )}>
                      {g.membershipType}
                    </Badge>
                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 tabular-nums">
                      {g.memberCount} member{g.memberCount === 1 ? '' : 's'}
                    </Badge>
                  </div>
                  {g.description && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">{g.description}</div>
                  )}
                  <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                    Added {new Date(g.addedAt).toLocaleDateString()}
                    {g.addedBy?.email && ` · by ${g.addedBy.email}`}
                  </div>
                </div>
                <Link
                  to={'/users/groups/$groupId' as never}
                  params={{ groupId: g.groupId } as never}
                  className="text-[11px] text-primary hover:underline inline-flex items-center gap-0.5"
                >
                  View <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
            ))}
          </div>
        )}
        <Button size="sm" variant="outline" className="mt-3" onClick={onAddToGroup}>
          <UsersRound className="h-3.5 w-3.5 mr-1.5" /> Add to group
        </Button>
      </SectionCard>

      {/* Closes #217: confirmation gate for the inline Remove control on
          custom-role rows. Mirrors the AlertDialog pattern used by the
          group-detail "Remove from group" flow so the affordance feels
          familiar to operators. */}
      <AlertDialog
        open={unassignTarget !== null}
        onOpenChange={(o) => {
          if (!o && !unassign.isPending) setUnassignTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove "{unassignTarget?.roleName}" from {user.fullName}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The user loses every permission this role grants
              immediately. The action is recorded in the audit trail
              and can be reversed by re-assigning the role from
              <strong> Assign role</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unassign.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleUnassignConfirm();
              }}
              disabled={unassign.isPending}
              className="bg-red-600 hover:bg-red-700 text-white focus-visible:ring-red-600"
            >
              {unassign.isPending ? 'Removing…' : 'Remove role'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Tab 3: Effective Permissions ───────────────────────────────────────────

function PermissionsTab({ user }: { user: User }) {
  const [search, setSearch] = useState('');

  /**
   * THE effective permissions, as the SERVER resolves them.
   *
   * B1-T2c review BF-02. This used to be computed HERE, in the browser: the
   * union of whatever `useRoles()` catalogue entries could be matched to
   * `user.roles` by a `role-<name>` / `system:<name>` string heuristic. That is
   * the "browser's own copy of the permission matrix" docs/system/rails/users.md
   * says must not exist, and it could not see an expired assignment, an
   * inactive or retired role, a group-held role or any non-CRUD grant — so the
   * tab showed authority `requirePermission` refuses (B1a-CO-8).
   *
   * `GET /users/:id/permissions` is a projection of `resolvePrincipal`, the one
   * function the gate reads, so the page and the gate now give one answer.
   * `actions` carries every granted action verbatim; `source` names the role or
   * group that carried them, as the server de-duplicated it.
   */
  const { permissions: rows, isLoading, error } = useUserEffectivePermissions(user.id);

  const effectivePermissions = useMemo(() => {
    const out: Array<{ resource: string; action: string; sources: string[] }> = [];
    for (const row of rows) {
      const sources = row.source
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      for (const action of row.actions) {
        out.push({ resource: row.resource, action, sources });
      }
    }
    return out.sort((a, b) =>
      `${a.resource}:${a.action}`.localeCompare(`${b.resource}:${b.action}`),
    );
  }, [rows]);

  const filtered = effectivePermissions.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.resource.toLowerCase().includes(q)
      || p.action.toLowerCase().includes(q)
      || p.sources.some(s => s.toLowerCase().includes(q));
  });

  // Group by resource
  const grouped = useMemo(() => {
    const g: Record<string, typeof filtered> = {};
    for (const p of filtered) {
      if (!g[p.resource]) g[p.resource] = [];
      g[p.resource].push(p);
    }
    return g;
  }, [filtered]);

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Effective Permissions</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isLoading
              ? 'Resolving…'
              : `${effectivePermissions.length} permission${effectivePermissions.length === 1 ? '' : 's'} in force, resolved by the server.`}
          </p>
        </div>
        <Input
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 w-64 text-xs"
        />
      </div>

      {/*
        No invented fallback: when the read fails the tab says so. A plausible
        default list here would be the page claiming authority nobody granted
        (principles.md #4 — nothing fails open).
      */}
      {error ? (
        <Card>
          <CardContent className="py-8 text-center text-xs text-muted-foreground">
            {getApiErrorMessage(error, 'Could not load effective permissions.')}
          </CardContent>
        </Card>
      ) : isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-xs text-muted-foreground italic">
            Loading effective permissions…
          </CardContent>
        </Card>
      ) : Object.keys(grouped).length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-xs text-muted-foreground">
            No permissions {search ? 'match your search' : 'assigned'}.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped).map(([resource, perms]) => (
            <Card key={resource}>
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-xs font-semibold capitalize">
                  {resource.replace(/_/g, ' ')}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-3 px-4">
                <div className="space-y-1.5">
                  {perms.map(p => (
                    <div key={`${p.resource}-${p.action}`} className="flex items-center gap-2 text-xs py-1">
                      <CheckCircle2 className="h-3 w-3 text-emerald-600 dark:text-emerald-400 shrink-0" />
                      <code className="text-[11px] font-mono bg-muted/50 px-1.5 py-0.5 rounded">
                        {p.action}:{p.resource}
                      </code>
                      <span className="text-[10px] text-muted-foreground ml-auto">
                        via {p.sources.join(', ')}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Tab 4: Access & Classification ─────────────────────────────────────────

function ClearanceTab({ user, onVerify }: { user: User; onVerify: () => void }) {
  return (
    <div className="space-y-4 max-w-5xl">
      <SectionCard title="Security clearance" icon={Shield}>
        <div className="flex items-center gap-3 mb-3">
          <ClearanceBadge level={user.clearance.level} showUnclassified />
          {user.clearance.caveats.length > 0 && (
            <div className="flex gap-1">
              {user.clearance.caveats.map(c => (
                <Badge key={c} variant="outline" className="text-[9px] bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30">
                  {c}
                </Badge>
              ))}
            </div>
          )}
        </div>
        {user.clearance.level !== 'unclassified' ? (
          <FieldGrid>
            <Field label="Granted by" value={user.clearance.grantedBy ?? '—'} />
            <Field label="Granted at" value={user.clearance.grantedAt ? new Date(user.clearance.grantedAt).toLocaleDateString() : '—'} />
            <Field
              label="Expires"
              value={user.clearance.expiresAt ? new Date(user.clearance.expiresAt).toLocaleDateString() : 'Does not expire'}
              highlight={user.clearance.expiresAt ? new Date(user.clearance.expiresAt).getTime() < Date.now() + 90 * 86400_000 : false}
            />
          </FieldGrid>
        ) : (
          <p className="text-xs text-muted-foreground italic">No clearance on record.</p>
        )}
        {user.clearance.history.length > 0 && (
          <>
            <Separator className="my-3" />
            <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">History</p>
            <div className="space-y-1.5">
              {user.clearance.history.map((h, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] py-1">
                  <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                  <span className="capitalize font-medium">{h.action}</span>
                  <span className="text-muted-foreground">→ {h.level.replace(/_/g, ' ')}</span>
                  <span className="text-muted-foreground ml-auto">by {h.by} · {new Date(h.at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </SectionCard>

      <SectionCard title="US Person verification" icon={UserIcon}>
        <div className="flex items-center gap-3 mb-3">
          <UsPersonIndicator verified={user.usPerson.verified} />
          <span className="text-[11px] text-muted-foreground">(ITAR §120.15)</span>
        </div>
        {user.usPerson.verified ? (
          <FieldGrid>
            <Field label="Verified by" value={user.usPerson.verifiedByName ?? '—'} />
            <Field label="Verified at" value={user.usPerson.verifiedAt ? new Date(user.usPerson.verifiedAt).toLocaleDateString() : '—'} />
            <Field
              label="Document type"
              value={user.usPerson.documentType ? user.usPerson.documentType.replace(/_/g, ' ') : '—'}
            />
            <Field
              label="Document expires"
              value={user.usPerson.expiresAt ? new Date(user.usPerson.expiresAt).toLocaleDateString() : 'N/A'}
            />
          </FieldGrid>
        ) : (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs flex-1">
              <p className="font-semibold text-amber-900 dark:text-amber-200">Not verified</p>
              <p className="text-amber-800 dark:text-amber-300 mt-0.5">
                User cannot access ITAR-controlled content until a security officer verifies their US Person status.
              </p>
            </div>
            <Button size="sm" variant="outline" className="shrink-0" onClick={onVerify}>
              Start verification
            </Button>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Classification & export access" icon={Lock}>
        <FieldGrid>
          <Field label="Classification tiers" value={
            user.classificationAccess.length === 0
              ? <span className="italic text-muted-foreground">None</span>
              : (
                <div className="flex flex-wrap gap-1">
                  {user.classificationAccess.map(t => <ClassificationBadge key={t} tier={t} />)}
                </div>
              )
          } />
          <Field label="Export control" value={
            user.exportControlAccess.length === 0
              ? <span className="italic text-muted-foreground">None</span>
              : (
                <div className="flex flex-wrap gap-1">
                  {user.exportControlAccess.map(t => (
                    <Badge key={t} variant="outline" className={cn(
                      'text-[10px] uppercase',
                      t === 'itar' && 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
                      t === 'ear' && 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
                      t === 'none' && 'bg-muted text-muted-foreground',
                    )}>
                      {EXPORT_CONTROL_LABEL[t]}
                    </Badge>
                  ))}
                </div>
              )
          } />
        </FieldGrid>
      </SectionCard>

      {(user.hipaaWorkforceMember || user.phiAccessLevel !== 'none') && (
        <SectionCard title="Healthcare (HIPAA)" icon={FileText}>
          <FieldGrid>
            <Field label="Workforce member" value={user.hipaaWorkforceMember ? 'Yes' : 'No'} />
            <Field label="PHI access level" value={<span className="capitalize">{user.phiAccessLevel}</span>} />
            <Field label="BAA on file" value={user.baaOnFile ? 'Yes' : 'N/A (FTE)'} />
          </FieldGrid>
        </SectionCard>
      )}
    </div>
  );
}

// ─── Tab 5: Sessions & Devices ───────────────────────────────────────────────

function SessionsTab({
  userId, userName, isSelf,
}: {
  userId: string;
  userName: string;
  isSelf: boolean;
}) {
  const { sessions, isLoading, error } = useUserSessions(userId);
  const revokeSession = useRevokeSession();
  const revokeImpersonation = useRevokeImpersonationSession();
  const canCrossUserRevoke = useHasPermission('lifecycle:lock', 'users');
  // Self-revoke is always allowed; cross-user requires lifecycle:lock.
  const canRevoke = isSelf || canCrossUserRevoke;
  // B2d-T2 — an impersonation row is ended through the impersonation surface,
  // and it costs what that surface costs. `DELETE /users/:id/sessions/:sid`
  // deletes a refresh token and would 404 on a session id.
  const canEndImpersonation = useHasPermission('impersonate', 'users');

  const DeviceIcon = (type: 'desktop' | 'mobile' | 'tablet') => {
    if (type === 'mobile') return Smartphone;
    if (type === 'tablet') return Tablet;
    return Monitor;
  };

  const handleRevoke = async (sessionId: string, kind: 'sign_in' | 'impersonation') => {
    try {
      if (kind === 'impersonation') {
        await revokeImpersonation.mutateAsync({ userId, sessionId });
        toast.success(`Impersonation of ${userName} ended`);
        return;
      }
      await revokeSession.mutateAsync({ userId, sessionId });
      toast.success(isSelf ? 'Session terminated' : `Session terminated for ${userName}`);
    } catch (err: any) {
      const code = getApiErrorCode(err);
      const msg = getApiErrorMessage(err, 'Failed to revoke session');
      toast.error(`${code}: ${msg}`);
    }
  };

  return (
    <div className="space-y-4 max-w-5xl">
      <SectionCard title={`Active sessions (${sessions.length})`} icon={Monitor}>
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading sessions…
          </div>
        ) : error ? (
          <p className="text-xs text-red-600 dark:text-red-400 italic">
            Failed to load sessions. Try refreshing the page.
          </p>
        ) : sessions.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            No active sessions for this user.
          </p>
        ) : (
          <div className="space-y-2">
            {sessions.map(s => {
              const isImpersonation = s.kind === 'impersonation';
              const Icon = isImpersonation ? Eye : DeviceIcon(s.deviceType);
              const mayRevoke = isImpersonation ? canEndImpersonation : canRevoke;
              return (
                <div
                  key={s.id}
                  className={`flex items-center gap-3 rounded-md border p-3${
                    isImpersonation ? ' border-amber-500/40 bg-amber-500/5' : ''
                  }`}
                >
                  <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium truncate">
                        {isImpersonation ? `Impersonated by ${s.startedBy ?? 'an administrator'}` : s.device}
                      </span>
                      {s.current && (
                        <Badge variant="outline" className="text-[9px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
                          Current session
                        </Badge>
                      )}
                      {/* B2d-T2 — somebody is INSIDE this account right now.
                          It is on this tab because "who is in my account" is
                          the question the tab answers. */}
                      {isImpersonation && (
                        <Badge variant="outline" className="text-[9px] bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30">
                          Impersonation
                        </Badge>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                      {s.ip || '—'} · Started {new Date(s.startedAt).toLocaleString()}
                      {isImpersonation && s.expiresAt && (
                        <> · Ends {new Date(s.expiresAt).toLocaleString()}</>
                      )}
                      {!isImpersonation && s.lastActiveAt && s.lastActiveAt !== s.startedAt && (
                        <> · Last active {new Date(s.lastActiveAt).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                  {mayRevoke && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-[11px] text-red-600 dark:text-red-400 shrink-0"
                      disabled={revokeSession.isPending || revokeImpersonation.isPending}
                      onClick={() => handleRevoke(s.id, s.kind)}
                      title={
                        isImpersonation
                          ? 'End this impersonation session now'
                          : isSelf ? 'End this session' : 'Force-end this session'
                      }
                    >
                      <XIcon className="h-3 w-3 mr-1" /> {isImpersonation ? 'End' : 'Revoke'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ─── Tab 6: Activity ─────────────────────────────────────────────────────────

function ActivityTab({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);
  const { events, pagination, isLoading, error } = useUserActivity(userId, page, 20);

  // Map W1a-02 type → icon + color.
  const typeStyles: Record<UserActivityEvent['type'], { icon: any; color: string; label: string }> = {
    login:                    { icon: CheckCircle2, color: 'text-emerald-600 dark:text-emerald-400', label: 'Login' },
    login_failed:             { icon: XCircle,      color: 'text-red-600 dark:text-red-400',         label: 'Failed login' },
    logout:                   { icon: ActivityIcon, color: 'text-muted-foreground',                  label: 'Logout' },
    token_refresh:            { icon: RotateCcw,    color: 'text-muted-foreground',                  label: 'Token refresh' },
    password_changed:         { icon: KeyRound,     color: 'text-blue-600 dark:text-blue-400',       label: 'Password changed' },
    password_reset:           { icon: KeyRound,     color: 'text-amber-600 dark:text-amber-400',     label: 'Password reset' },
    login_context_selected:   { icon: CheckCircle2, color: 'text-emerald-600 dark:text-emerald-400', label: 'Tenant selected' },
    mfa_reset:                { icon: ShieldAlert,  color: 'text-amber-600 dark:text-amber-400',     label: 'MFA reset' },
    impersonation_started:    { icon: Eye,          color: 'text-amber-600 dark:text-amber-400',     label: 'Impersonation started' },
    impersonation_ended:      { icon: Eye,          color: 'text-muted-foreground',                  label: 'Impersonation ended' },
    session_revoked:          { icon: XIcon,        color: 'text-red-600 dark:text-red-400',         label: 'Session revoked' },
    token_rotation_violation: { icon: AlertTriangle,color: 'text-red-600 dark:text-red-400',         label: 'Rotation violation' },
    other:                    { icon: ActivityIcon, color: 'text-muted-foreground',                  label: 'Other' },
  };

  const totalCount = pagination?.totalCount ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  return (
    <div className="space-y-4 max-w-5xl">
      <SectionCard title={`Activity (${totalCount})`} icon={ActivityIcon}>
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading activity…
          </div>
        ) : error ? (
          <p className="text-xs text-red-600 dark:text-red-400 italic">
            Failed to load activity. Try refreshing the page.
          </p>
        ) : events.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            No activity recorded yet. Sign-ins, sign-outs, password changes and other identity events will appear here.
          </p>
        ) : (
          <>
            <div className="space-y-1">
              {events.map(a => {
                const style = typeStyles[a.type];
                const Icon = style.icon;
                return (
                  <div key={a.id} className="flex items-start gap-3 py-2 border-b last:border-b-0">
                    <Icon className={cn('h-4 w-4 shrink-0 mt-0.5', style.color)} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs">
                        <span className="font-medium">{style.label}</span>
                        {a.details && (
                          <span className="text-muted-foreground"> · {a.details}</span>
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                        {new Date(a.timestamp).toLocaleString()}
                        {a.ip && <> · {a.ip}</>}
                        {a.userAgent && (() => {
                          // Closes #257: title and aria-label use the
                          // SAME humanised summary, not the raw UA.
                          const ua = summariseUserAgent(a.userAgent);
                          return (
                            <>
                              {' · '}
                              <span title={ua} aria-label={ua}>
                                {ua}
                              </span>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <Badge variant="outline" className={cn(
                      'text-[9px] uppercase',
                      a.outcome === 'failure' && 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
                    )}>
                      {a.outcome}
                    </Badge>
                  </div>
                );
              })}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t">
                <div className="text-[10px] text-muted-foreground">
                  Page {page} of {totalPages} · {totalCount} events
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </SectionCard>
    </div>
  );
}

/**
 * Map raw action codes (UPPER_SNAKE) to a short human label. Unknown
 * codes fall back to a Title-Cased version of the code so we never render
 * raw enum strings to admins. Keep this list intentionally short — only
 * actions that actually appear on the user audit feed are mapped; novel
 * actions surface their derived label until added.
 */
const AUDIT_ACTION_LABELS: Record<string, string> = {
  USER_CREATED: 'User created',
  USER_UPDATED: 'Profile updated',
  USER_DELETED: 'User deleted',
  USER_STATUS_CHANGED: 'Status changed',
  USER_LOCKED: 'Account locked',
  USER_UNLOCKED: 'Account unlocked',
  USER_SUSPENDED: 'Account suspended',
  USER_RESTORED: 'Account restored',
  USER_DEACTIVATED: 'Account deactivated',
  USER_ROLE_CHANGED: 'Role changed',
  IDENTITY_FIELDS_UPDATED: 'Compliance fields updated',
  US_PERSON_VERIFIED: 'US Person verified',
  CLEARANCE_GRANTED: 'Clearance granted',
  CLEARANCE_REVOKED: 'Clearance revoked',
  GROUP_MEMBER_ADDED: 'Added to group',
  GROUP_MEMBER_REMOVED: 'Removed from group',
  ROLE_ASSIGNED: 'Role assigned',
  ROLE_UNASSIGNED: 'Role unassigned',
  SESSION_REVOKED: 'Session revoked',
  PASSWORD_CHANGED: 'Password changed',
  PASSWORD_RESET: 'Password reset',
  // B2e-T1 review (R4) — `MFA_ENABLED` and `MFA_DISABLED` were labelled here
  // and are written by nothing: no route sets `users.mfa_enabled` to true, so
  // neither action has ever reached this table. They were removed from the
  // vocabulary and from the audit registry in the same change; a label for an
  // action the registry does not carry is the dead half of the pair the
  // vocabulary test now fails on.
  MFA_RESET: 'MFA reset',
  LOGIN: 'Signed in',
  LOGIN_FAILED: 'Sign-in failed',
  LOGOUT: 'Signed out',
  IMPERSONATION_STARTED: 'Impersonation started',
  IMPERSONATION_ENDED: 'Impersonation ended',
  INVITATION_CREATED: 'Invitation created',
  INVITATION_RESENT: 'Invitation resent',
  INVITATION_REVOKED: 'Invitation revoked',
  INVITATION_ACCEPTED: 'Invitation accepted',
};

/**
 * Humanise raw `action` strings for the audit row.
 *
 * The platform audit logger captures actions in two shapes:
 *
 *   1. **Semantic UPPER_SNAKE**: `USER_STATUS_CHANGED`, `LOGIN`, etc.
 *      Mapped via `AUDIT_ACTION_LABELS`; unmapped codes fall back to
 *      Title Case (`SOME_NEW_ACTION` → "Some New Action").
 *
 *   2. **HTTP method + URL**: `POST /api/v1/conversations`, `DELETE
 *      /api/v1/playbooks/abc-123`. Many backend handlers fall through
 *      to the generic request-logging plugin (cross-module bug CM-1)
 *      and end up storing the URL as the action. We render those as
 *      `<verb> <noun>` so the admin sees something readable: e.g.
 *      "Created conversation", "Deleted playbook", "Updated automation".
 *      The trailing UUID / numeric segment is dropped so multiple rows
 *      of the same shape collapse to the same human label.
 */
function humanizeAuditAction(code: string): string {
  if (AUDIT_ACTION_LABELS[code]) return AUDIT_ACTION_LABELS[code];

  // URL-shape: METHOD<space>/api/v1/<resource>[/...]
  const urlMatch = /^(GET|POST|PATCH|PUT|DELETE)\s+\/api\/v\d+\/([^?\s]+)/i.exec(code);
  if (urlMatch) {
    const method = urlMatch[1].toUpperCase();
    const path = urlMatch[2];
    // Strip trailing UUID/UUID-like/numeric path segments so list-style
    // and detail-style routes collapse to the same label.
    const segments = path.split('/').filter((seg) => {
      if (!seg) return false;
      // UUID v4-ish (8-4-4-4-12 hex)
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) return false;
      // Pure numeric ids
      if (/^\d+$/.test(seg)) return false;
      return true;
    });
    const verbMap: Record<string, string> = {
      GET: 'Viewed',
      POST: 'Created',
      PATCH: 'Updated',
      PUT: 'Updated',
      DELETE: 'Deleted',
    };
    const verb = verbMap[method] ?? method;
    if (segments.length === 0) return verb;
    // Use the most specific resource segment as the noun. Drop common
    // module prefixes like "users", "admin", and similar grouping nodes
    // so "POST /api/v1/agent-approvals/<id>/comments" → "Created comment"
    // rather than "Created comments".
    const noun = segments[segments.length - 1].replace(/-/g, ' ').replace(/_/g, ' ').toLowerCase();
    // Drop trailing s for a cleaner verb+singular noun feel ("Created
    // conversation" reads better than "Created conversations").
    const singularNoun = noun.endsWith('s') && !noun.endsWith('ss') ? noun.slice(0, -1) : noun;
    return `${verb} ${singularNoun}`;
  }

  // Default: Title-Case the snake_case enum.
  return code
    .toLowerCase()
    .split('_')
    .map((part) => (part.length > 0 ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');
}

/**
 * Audit details may arrive as either:
 *   - a plain string ("New password sent to user@example.com")
 *   - a JSON-encoded payload string ({"from":"active","to":"locked","reason":"…"})
 *   - already a parsed object (the API normally stringifies, but defensive)
 *   - null/undefined
 *
 * Render a readable summary instead of dumping raw JSON or "[object Object]"
 * to the admin. Only render scalar values; nested objects collapse to a
 * compact `key=…` notation so the row stays one-line-friendly.
 */
function formatAuditDetails(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  let payload: unknown = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        payload = JSON.parse(trimmed);
      } catch {
        return trimmed;
      }
    } else {
      return trimmed;
    }
  }
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const entries = Object.entries(payload as Record<string, unknown>)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => {
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
          return `${k}: ${v}`;
        }
        return `${k}: ${JSON.stringify(v)}`;
      });
    if (entries.length === 0) return null;
    return entries.join(' · ');
  }
  if (Array.isArray(payload)) {
    return payload.map((p) => (typeof p === 'object' ? JSON.stringify(p) : String(p))).join(', ');
  }
  return String(payload);
}

// ─── Tab 7: Audit Trail ──────────────────────────────────────────────────────

function AuditTrailTab({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);
  const { events, pagination, isLoading, error } = useUserAudit(userId, page, 20);

  // Hash-chain integrity status — Bug #1088.
  //
  // The audit-log hash chain is a GLOBAL chain across every tenant audit
  // row (each row's previousHash links to the row immediately before it
  // in insertion order, regardless of which user/resource it targets).
  //
  // The events list here is a per-user FILTERED SUBSET of that global
  // chain (the API returns only rows where resource === userId). After a
  // normal create/update there will be intervening rows from other users
  // or other tenants between the visible rows — so the visible subset is
  // NOT expected to link previousHash → recordHash chronologically. The
  // prior verifier compared consecutive items in the subset and flagged
  // `broken` on every normal mutation, surfacing a confusing "INTEGRITY
  // ERROR" banner the moment QA created+edited a single test user.
  //
  // Correct semantics for a filtered subset: we can verify that each
  // row's own hash columns are populated (proves the chain is being
  // written), but cross-row linkage can only be verified by the full
  // /trust/audit verifier that walks the unfiltered chain. Show:
  //   - 'pending' when rows are missing hash columns (pre-W1a-26 retrofit)
  //   - 'verified-subset' when every visible row has hash + sequence
  //     populated (links to the full-chain verifier in /trust/audit)
  //   - 'broken' is intentionally never emitted from this filtered view.
  const chainStatus = useMemo<'verified-subset' | 'pending'>(() => {
    if (events.length === 0) return 'pending';
    const allPopulated = events.every(
      (e) => e.recordHash && e.sequenceNumber,
    );
    return allPopulated ? 'verified-subset' : 'pending';
  }, [events]);

  const totalCount = pagination?.totalCount ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  return (
    <div className="space-y-4 max-w-5xl">
      {/* Bug #1088 — the per-user audit view is a FILTERED subset of the
          global hash chain. Cross-row linkage cannot be checked locally;
          surface "verified subset" with a deep-link to the full-chain
          verifier in /trust/audit instead of falsely raising an integrity
          error on every normal mutation. */}
      <div className={cn(
        'rounded-md border-l-4 bg-card p-3 flex items-start gap-2',
        chainStatus === 'verified-subset' && 'border-l-emerald-500',
        chainStatus === 'pending' && 'border-l-amber-500',
      )} data-testid="user-audit-chain-banner">
        <Hash className={cn(
          'h-4 w-4 shrink-0 mt-0.5',
          chainStatus === 'verified-subset' && 'text-emerald-600 dark:text-emerald-400',
          chainStatus === 'pending' && 'text-amber-600 dark:text-amber-400',
        )} />
        <div className="text-xs flex-1">
          <p className="font-semibold">
            {chainStatus === 'verified-subset' && 'Hash chain — filtered subset'}
            {chainStatus === 'pending' && 'Hash chain — pending'}
          </p>
          <p className="text-muted-foreground mt-0.5">
            {chainStatus === 'verified-subset' && 'Every visible row carries a sequence number and record hash, so the global chain is being written. Cross-row linkage can only be verified against the full unfiltered chain — use the link on the right.'}
            {chainStatus === 'pending' && 'Audit-row hash-chaining lands with W1a-26 (Stage 5). Sequence and timestamps are accurate today; cryptographic linkage will be retrofit.'}
          </p>
        </div>
        <Link to="/trust/audit" className="text-[11px] text-primary hover:underline inline-flex items-center gap-0.5 shrink-0">
          Verify full chain <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      <SectionCard title={`Audit events (${totalCount})`} icon={Hash}>
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading audit trail…
          </div>
        ) : error ? (
          <p className="text-xs text-red-600 dark:text-red-400 italic">
            Failed to load audit trail. Try refreshing the page.
          </p>
        ) : events.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            No audit events. Administrative actions on this user (status changes, identity-field updates, role assignments, session revocation) will appear here.
          </p>
        ) : (
          <>
            <div className="space-y-2">
              {events.map(e => {
                const label = humanizeAuditAction(e.action);
                const formattedDetails = formatAuditDetails(e.details);
                return (
                <div key={e.id} className="rounded-md border p-3">
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium">{label}</span>
                        <code className="text-[9px] font-mono bg-muted/60 px-1 py-0.5 rounded text-muted-foreground" title={e.action}>
                          {e.action}
                        </code>
                        {e.sequenceNumber && (
                          <span className="text-[10px] text-muted-foreground tabular-nums">
                            seq #{e.sequenceNumber}
                          </span>
                        )}
                        {e.outcome === 'failure' && (
                          <Badge variant="outline" className="text-[9px] bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30">
                            failure
                          </Badge>
                        )}
                      </div>
                      {formattedDetails && (
                        <div className="text-xs mt-1 break-words text-muted-foreground">{formattedDetails}</div>
                      )}
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {e.actorEmail ? <>by {e.actorEmail} · </> : null}
                        {new Date(e.timestamp).toLocaleString()}
                        {e.ipAddress && <> · {e.ipAddress}</>}
                      </div>
                    </div>
                  </div>
                  {(e.recordHash || e.previousHash) && (
                    <>
                      <Separator className="my-2" />
                      <div className="space-y-0.5 text-[10px] font-mono">
                        {e.recordHash && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground w-20">Hash:</span>
                            <code className="truncate">{e.recordHash.slice(0, 32)}…</code>
                          </div>
                        )}
                        {e.previousHash && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground w-20">Previous:</span>
                            <code className="truncate">{e.previousHash.slice(0, 32)}…</code>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                );
              })}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t">
                <div className="text-[10px] text-muted-foreground">
                  Page {page} of {totalPages} · {totalCount} events
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </SectionCard>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function SectionCard({
  title, icon: Icon, children,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 px-4 pb-4">{children}</CardContent>
    </Card>
  );
}

function FieldGrid({ children, compact = false }: { children: React.ReactNode; compact?: boolean }) {
  return (
    <div className={cn(
      'grid gap-x-4 gap-y-2',
      compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2',
    )}>
      {children}
    </div>
  );
}

function Field({
  label, value, icon: Icon, copy = false, highlight = false,
}: {
  label: string;
  value: React.ReactNode;
  icon?: any;
  copy?: boolean;
  highlight?: boolean;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-0.5">
        {label}
      </div>
      <div className={cn(
        'text-xs flex items-center gap-1.5',
        highlight && 'text-amber-700 dark:text-amber-400 font-semibold',
      )}>
        {Icon && <Icon className="h-3 w-3 text-muted-foreground shrink-0" />}
        <span className="min-w-0 truncate">{value}</span>
        {copy && typeof value === 'string' && (
          <button
            onClick={() => {
              navigator.clipboard.writeText(value);
              toast.success('Copied');
            }}
            className="text-muted-foreground hover:text-foreground shrink-0"
          >
            <Copy className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}
